import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStore } from '../context/StoreContext';

const { FiFileText, FiUploadCloud, FiTrash2, FiDownload, FiEye, FiX, FiFile, FiImage, FiPlus } = FiIcons;

const DOC_TYPE_ICONS = { email: '✉️', lease: '📋', letter: '📄', 'move-in': '🏠', 'move-out': '📦', inspection: '🔍', receipt: '🧾', other: '📝', upload: '📎' };

// ─── Document Viewer Modal ───────────────────────────────────────────
const DocViewerModal = ({ isOpen, onClose, doc }) => {
  if (!isOpen || !doc) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[90] p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-sm font-bold text-gray-900">{doc.name}</h3>
            <p className="text-[10px] text-gray-400">{doc.date} · {doc.type || doc.docType || 'Document'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <SafeIcon icon={FiX} size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {doc.fileData ? (
            doc.fileType?.startsWith('image/') ? (
              <img src={doc.fileData} alt={doc.name} className="max-w-full rounded-lg" />
            ) : (
              <div className="text-center py-12">
                <SafeIcon icon={FiFile} size={48} className="text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-500">{doc.fileName}</p>
                <a href={doc.fileData} download={doc.fileName} className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700">
                  <SafeIcon icon={FiDownload} size={14} /> Download
                </a>
              </div>
            )
          ) : doc.content ? (
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 text-sm text-gray-800 leading-relaxed font-serif whitespace-pre-wrap">
                {doc.content}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-12">No preview available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Documents Section ──────────────────────────────────────────
const DocumentsSection = ({ recordType, recordId, title }) => {
  const { data, addRecord, deleteRecord } = useStore();
  const [viewingDoc, setViewingDoc] = useState(null);

  // Get documents linked to this record
  const linkedDocs = (data.documents || []).filter(d => {
    if (recordType === 'property') return d.propertyId === recordId;
    if (recordType === 'tenant') return d.tenantId === recordId;
    return false;
  }).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('File must be under 10MB'); return; }

    const reader = new FileReader();
    reader.onload = () => {
      addRecord('documents', {
        name: file.name,
        fileName: file.name,
        fileData: reader.result,
        fileType: file.type,
        fileSize: file.size,
        type: 'Upload',
        docType: 'upload',
        date: new Date().toISOString().split('T')[0],
        propertyId: recordType === 'property' ? recordId : '',
        tenantId: recordType === 'tenant' ? recordId : '',
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title || 'Documents'}</h4>
        <label className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-semibold cursor-pointer hover:bg-indigo-100 transition-colors">
          <SafeIcon icon={FiUploadCloud} size={12} /> Upload
          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.txt,.csv" onChange={handleFileUpload} />
        </label>
      </div>

      {linkedDocs.length === 0 ? (
        <label className="flex flex-col items-center justify-center py-6 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/20 transition-colors">
          <SafeIcon icon={FiUploadCloud} size={20} className="text-gray-300 mb-1.5" />
          <span className="text-xs text-gray-400 font-medium">No documents yet</span>
          <span className="text-[10px] text-gray-300 mt-0.5">Click to upload or generate from templates</span>
          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.txt,.csv" onChange={handleFileUpload} />
        </label>
      ) : (
        <div className="space-y-1.5">
          {linkedDocs.map(doc => (
            <div key={doc.id} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group">
              <div className="flex items-center gap-2.5 min-w-0 flex-1" >
                <span className="text-base shrink-0">{DOC_TYPE_ICONS[doc.docType] || '📄'}</span>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate">{doc.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-gray-400">{doc.date}</span>
                    {doc.type && <span className="text-[10px] text-indigo-500">{doc.type}</span>}
                    {doc.fileSize && <span className="text-[10px] text-gray-400">{formatSize(doc.fileSize)}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <button onClick={() => setViewingDoc(doc)} title="View"
                  className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                  <SafeIcon icon={FiEye} size={13} />
                </button>
                <button onClick={() => deleteRecord('documents', doc.id)} title="Delete"
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                  <SafeIcon icon={FiTrash2} size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <DocViewerModal isOpen={!!viewingDoc} onClose={() => setViewingDoc(null)} doc={viewingDoc} />
    </div>
  );
};

export default DocumentsSection;
